import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatQty, matchesSearchTerms, normalizeSearch, searchTerms } from '@pos/core';
import { useSession } from '../../app/current.tsx';
import { Button, Card, Input, PageHeader, Select, Spinner, Stat, cx } from '../../ui/index.tsx';
import { AlertIcon, BoxIcon, ReceiptIcon } from '../../ui/icons.tsx';
import { useCatalog, useMoney } from '../products/data.ts';
import { AdjustDialog } from './AdjustDialog.tsx';
import { STATUS_STYLE, formatStock, isAlert, useStockMap, useStockRows, type StockRow } from './data.ts';

const PAGE = 100;
type Filter = 'all' | 'alerts' | 'low' | 'out' | 'negative' | 'untracked';

export function StockPage() {
  const { can } = useSession();
  const catalog = useCatalog();
  const stock = useStockMap();
  const rows = useStockRows(catalog, stock);
  const money = useMoney();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [adjusting, setAdjusting] = useState<StockRow | null>(null);
  const filter = (params.get('estado') as Filter) || 'all';

  const canAdjust = can('adjust_stock') || can('receive_purchases');
  const showCosts = can('view_costs');

  const filtered = useMemo(() => {
    const terms = searchTerms(query);
    return rows.filter((r) => {
      if (!r.item.product.active || !r.variant.active) return false;
      if (filter === 'alerts' && !isAlert(r.status)) return false;
      if (filter !== 'all' && filter !== 'alerts' && r.status !== filter) return false;
      return matchesSearchTerms(`${r.item.search} ${normalizeSearch(r.label)}`, terms);
    });
  }, [rows, query, filter]);

  const summary = useMemo(() => {
    let value = 0;
    let valued = 0;
    let alerts = 0;
    for (const r of rows) {
      if (!r.item.product.active || !r.variant.active) continue;
      if (isAlert(r.status)) alerts++;
      if (r.tracked && r.qty > 0 && r.cost !== undefined) {
        value += Math.round((r.qty * r.cost) / 1000);
        valued++;
      }
    }
    return { value, valued, alerts };
  }, [rows]);

  if (!catalog || !stock) {
    return (
      <div className="flex justify-center py-12 text-slate-500">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Stock"
        description="Lo que hay de cada producto. Se calcula con todos los movimientos: compras, ventas, ajustes y mermas."
        actions={
          <>
            {can('adjust_stock') && (
              <Link to="/stock/conteo">
                <Button variant="secondary">Toma de inventario</Button>
              </Link>
            )}
            {can('receive_purchases') && (
              <Link to="/stock/compra">
                <Button>Cargar compra</Button>
              </Link>
            )}
          </>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <button
          type="button"
          onClick={() => setParams(filter === 'alerts' ? {} : { estado: 'alerts' })}
          className={cx('rounded-xl bg-white p-4 text-left shadow-card ring-1 transition hover:ring-brand', summary.alerts ? 'ring-amber-300' : 'ring-slate-200/80')}
        >
          <span className="flex items-start justify-between gap-2">
            <span className="text-sm font-medium text-slate-500">Con alertas</span>
            <span className={cx('-mt-0.5 flex size-8 items-center justify-center rounded-lg', summary.alerts ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' : 'bg-slate-100 text-slate-600')}>
              <AlertIcon className="size-4" />
            </span>
          </span>
          <span className={cx('mt-1 block text-2xl font-semibold tracking-tight tabular-nums', summary.alerts > 0 && 'text-amber-700')}>{summary.alerts}</span>
          <span className="mt-1 block text-xs text-slate-500">Stock bajo, sin stock o negativo</span>
        </button>
        {showCosts && (
          <Stat label="Valor del inventario (a costo)" icon={BoxIcon} value={money(summary.value)} foot={`${summary.valued} productos con stock y costo cargado`} />
        )}
        {showCosts && (
          <Link to="/stock/compras" className="group flex flex-col justify-between rounded-xl bg-white p-4 shadow-card ring-1 ring-slate-200/80 transition hover:ring-brand">
            <span className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium text-slate-500">Compras</span>
              <span className="-mt-0.5 flex size-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                <ReceiptIcon className="size-4" />
              </span>
            </span>
            <span className="mt-1 font-semibold text-brand">
              Ver compras cargadas <span className="inline-block transition-transform group-hover:translate-x-0.5">→</span>
            </span>
          </Link>
        )}
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-[1fr_auto]">
        <Input
          type="search"
          placeholder="Buscar por nombre, código o categoría…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setLimit(PAGE);
          }}
          aria-label="Buscar en el stock"
        />
        <Select value={filter} onChange={(e) => setParams(e.target.value === 'all' ? {} : { estado: e.target.value })} aria-label="Estado del stock">
          <option value="all">Todos</option>
          <option value="alerts">Con alertas</option>
          <option value="low">Stock bajo</option>
          <option value="out">Sin stock</option>
          <option value="negative">Negativo</option>
          <option value="untracked">Sin control de stock</option>
        </Select>
      </div>

      <Card flush className="divide-y divide-slate-100">
        {filtered.length === 0 && <p className="p-6 text-center text-sm text-slate-500">No hay productos que coincidan.</p>}
        {filtered.slice(0, limit).map((r) => (
          <div key={r.variant.id} className="flex items-center gap-3 p-3 sm:px-4">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{r.label}</p>
              <p className="truncate text-sm text-slate-500">
                {[r.item.category?.name, r.variant.min_stock != null && `mínimo ${formatQty(r.variant.min_stock, r.item.product.unit_type)}`]
                  .filter(Boolean)
                  .join(' · ') || 'Sin categoría'}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className={cx('text-lg font-semibold tabular-nums', STATUS_STYLE[r.status].className)}>{formatStock(r)}</p>
              {r.status !== 'ok' && <p className={cx('text-xs', STATUS_STYLE[r.status].className)}>{STATUS_STYLE[r.status].label}</p>}
            </div>
            <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
              {canAdjust && (
                <Button variant="secondary" size="sm" onClick={() => setAdjusting(r)}>
                  Ajustar
                </Button>
              )}
              <Link to={`/stock/movimientos/${r.variant.id}`}>
                <Button variant="ghost" size="sm">
                  Movimientos
                </Button>
              </Link>
            </div>
          </div>
        ))}
      </Card>
      {filtered.length > limit && (
        <div className="mt-4 text-center">
          <Button variant="secondary" onClick={() => setLimit((l) => l + PAGE)}>
            Mostrar más ({filtered.length - limit} restantes)
          </Button>
        </div>
      )}

      <AdjustDialog row={adjusting} onClose={() => setAdjusting(null)} />
    </div>
  );
}
